import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  FiservCardStatusEnum,
  Inscription,
  Preloader,
  RoundPendingIcon,
  useResponsive,
} from '@common-fe/common-fe';

import { ModalWrapper } from '@/components/wrappers';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import { useGetCardsQuery } from '@/modules/transaction/queries/useGetCards.query';
import useRemoveDependentQuery from '@/modules/user/components/ManageProfile/components/Dependents/queries/useRemoveDependent.query';
import { useStore } from '@/modules/user/components/ManageProfile/components/Dependents/stores/useDependents.store';
import { useAuthStore } from '@/modules/user/stores';

interface Props {
  onSetVisible: (value: boolean) => void;
}

const RemoveDependentModal: React.FC<Props> = ({ onSetVisible }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { editableDependent, handleReset } = useStore();
  const fullName = useMemo(
    () => `${editableDependent?.firstName || ''} ${editableDependent?.lastName || ''}`.trim(),
    [editableDependent]
  );
  const { user } = useAuthStore();
  const { response } = useGetCardsQuery(user?.employee?.id, editableDependent?.id);
  const hasActiveCard = useMemo(
    () => Boolean(response?.grouped_cards?.[FiservCardStatusEnum.ACTIVE]?.length),
    [response]
  );
  const { removeDependent, isLoading } = useRemoveDependentQuery();

  const handleClose = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const onSuccess = useCallback(() => {
    handleClose();
    handleAddPermanentSnackbar({
      text: `${fullName} was successfully deactivated`,
      state: SnackbarState.positive,
      closeIcon: true,
      autoRemove: true,
    });
  }, [fullName, handleClose, handleAddPermanentSnackbar]);

  const handleSubmit = useCallback(async () => {
    const dependentId = editableDependent?.id;
    if (dependentId) {
      await removeDependent(dependentId);
      onSuccess();
    }
  }, [editableDependent, onSuccess, removeDependent]);

  useEffect(() => {
    return () => {
      handleReset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ModalWrapper visible onSetVisible={onSetVisible}>
      <Box align="center" pad={{ vertical: 'spacing24' }}>
        <RoundPendingIcon color="warning" />
        <Inscription margin={{ top: 'spacing12' }} size="2xl" weight={700}>
          {t('Deactivate dependent?')}
        </Inscription>
        {hasActiveCard ? (
          <Box align="center" margin={{ top: 'spacing12' }}>
            <Inscription color="textBody" lineHeight="20px">
              A card has been found for {fullName}.
            </Inscription>
            <Inscription color="textBody" lineHeight="20px">
              By deleting this dependent, their card will also be closed.
            </Inscription>
          </Box>
        ) : null}
        <Box
          margin={{ top: 'spacing24' }}
          fill={isMobile}
          gap="spacing12"
          direction={isMobile ? 'column-reverse' : 'row'}
        >
          <AppButton
            fill={isMobile}
            onClick={handleClose}
            buttonType="secondary"
            width="140px"
            testId="cancel"
          >
            {t('Cancel')}
          </AppButton>

          <AppButton fill={isMobile} onClick={handleSubmit} color="red" width="140px" testId="remove">
            {isLoading ? <Preloader color="error" /> : t('Deactivate')}
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default RemoveDependentModal;
